// Versão: 1.03
// Data..: 2026-09-24
// Contexto de autenticação: sessão, perfil (/me), troca de senha no 1º acesso e MFA (TOTP).
// v1.03: MFA obrigatório — expõe o passo de configuração de MFA no login (totp_setup_required).

import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import type { User } from '@makweb/shared';
import { getToken, signIn as cognitoSignIn, signOut as cognitoSignOut, type SignInResult } from '../lib/auth.js';
import { api } from '../lib/api.js';

// Resultado do login exposto à UI: concluído, aguardando nova senha, aguardando MFA
// ou exigindo a configuração de um app autenticador (MFA obrigatório sem TOTP ativo).
export type LoginOutcome =
  | { status: 'ok' }
  | { status: 'new_password_required'; setNewPassword: (newPassword: string) => Promise<LoginOutcome> }
  | { status: 'totp_required'; submitTotp: (code: string) => Promise<void> }
  | { status: 'totp_setup_required'; secret: string; confirmSetup: (code: string) => Promise<void> };

interface AuthContextValue {
  user: User | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<LoginOutcome>;
  signOut: () => void;
}

const AuthContext = createContext<AuthContextValue>({
  user: null,
  loading: true,
  signIn: async () => ({ status: 'ok' }),
  signOut: () => {},
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // Ao montar, tenta recuperar a sessão e carregar o perfil.
  useEffect(() => {
    (async () => {
      const token = await getToken();
      if (token) {
        try {
          setUser(await api.get<User>('/me'));
        } catch {
          setUser(null);
        }
      }
      setLoading(false);
    })();
  }, []);

  // Carrega o perfil após obter um token válido.
  const loadProfile = async () => setUser(await api.get<User>('/me'));

  // Converte o resultado bruto do Cognito no desfecho exposto à UI, embrulhando as
  // funções para recarregar o perfil ao concluir. Trata o encadeamento de desafios
  // (ex.: nova senha → configurar MFA).
  const toOutcome = (result: SignInResult): LoginOutcome | Promise<LoginOutcome> => {
    if (result.status === 'ok') {
      return loadProfile().then(() => ({ status: 'ok' as const }));
    }
    // MFA por app já ativo: devolve a função para enviar o código de 6 dígitos.
    if (result.status === 'totp_required') {
      return {
        status: 'totp_required',
        submitTotp: async (code: string) => {
          await result.submitTotp(code);
          await loadProfile();
        },
      };
    }
    // MFA obrigatório sem app: devolve o segredo (QR) e a confirmação do código.
    if (result.status === 'totp_setup_required') {
      return {
        status: 'totp_setup_required',
        secret: result.secret,
        confirmSetup: async (code: string) => {
          await result.confirmSetup(code);
          await loadProfile();
        },
      };
    }
    // Primeiro acesso: definir a senha pode encadear o passo de MFA (novo outcome).
    return {
      status: 'new_password_required',
      setNewPassword: async (newPassword: string) => toOutcome(await result.setNewPassword(newPassword)),
    };
  };

  const signIn = async (email: string, password: string): Promise<LoginOutcome> =>
    toOutcome(await cognitoSignIn(email, password));

  const signOut = () => {
    cognitoSignOut();
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
